import React, {useContext} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import type {ButtonColumn, DataGridColumn, DataGridServerProps, TextColumn} from '@igp/shared/components/data-grid';
import {DataGridServer} from '@igp/shared/components/data-grid';
import {ActionConfirmationModal, ModalContext} from '@igp/shared/components/modal';
import {formatTimestamp} from '@igp/shared/utils';

import {useDebtResponsibleMasterAgentActions} from '../hooks';
import type {DebtResponsibleMasterAgentViewModel, DebtResponsibleMasterAgentViewModelKeys} from '../types';

export const localized = defineMessages({
    agent_uid: {
        id: 'DebtResponsibleMasterAgentColumn_agent_uid',
        defaultMessage: 'Agent ID',
    },
    aggregated_downline_debt: {
        id: 'DebtResponsibleMasterAgentColumn_aggregated_downline_debt',
        defaultMessage: 'Aggregated Downline Debt',
    },
    created_at: {
        id: 'DebtResponsibleMasterAgentColumn_created_at',
        defaultMessage: 'Created At',
    },
    created_by: {
        id: 'DebtResponsibleMasterAgentColumn_created_by',
        defaultMessage: 'Created By',
    },
    downline_agents_count: {
        id: 'DebtResponsibleMasterAgentColumn_downline_agents_count',
        defaultMessage: 'Number of Downline Agents',
    },
    username: {
        id: 'DebtResponsibleMasterAgentColumn_username',
        defaultMessage: 'Username',
    },
    removeButton: {
        id: 'DebtResponsibleMasterAgentDataGrid_empty',
        defaultMessage: 'Remove',
    },
    removeModalTitle: {
        id: 'DebtResponsibleMasterAgentDataGrid_removeModalTitle',
        defaultMessage: 'Remove Debt Responsible Master Agent',
    },
    removeModalDescription: {
        id: 'DebtResponsibleMasterAgentDataGrid_removeModalDescription',
        defaultMessage:
            'Are you sure you want to remove this agent from the Debt Responsible Master Agent list? This will prevent them from inheriting any debts starting next rollup cycle.',
    },
    cancelButtonLabel: {
        id: 'DebtResponsibleMasterAgentDataGrid_cancelButtonLabel',
        defaultMessage: 'Cancel',
    },
    confirmButtonLabel: {
        id: 'DebtResponsibleMasterAgentDataGrid_confirmButtonLabel',
        defaultMessage: 'Confirm',
    },
});

export type DebtResponsibleMasterAgentColumns = DebtResponsibleMasterAgentViewModelKeys | 'remove_button';

function useColumns(columns: DebtResponsibleMasterAgentColumns[]): DataGridColumn<DebtResponsibleMasterAgentViewModel>[] {
    const {formatMessage, formatNumber} = useIntl();
    const {openModal, closeModal} = useContext(ModalContext);
    const {removeDebtResponsibleMasterAgent} = useDebtResponsibleMasterAgentActions();

    const agent_id: TextColumn<DebtResponsibleMasterAgentViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.agent_uid,
        field: 'agent_uid',
        title: localized.agent_uid,
        sortable: true,
    };

    const username: TextColumn<DebtResponsibleMasterAgentViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.username,
        field: 'username',
        title: localized.username,
        sortable: true,
    };

    const created_at: TextColumn<DebtResponsibleMasterAgentViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => (m.created_at ? formatTimestamp(m.created_at, 'date') : null),
        field: 'created_at',
        title: localized.created_at,
        sortable: true,
    };

    const created_by: TextColumn<DebtResponsibleMasterAgentViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.created_by,
        field: 'created_by',
        title: localized.created_by,
        sortable: true,
    };

    const aggregated_downline_debt: TextColumn<DebtResponsibleMasterAgentViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m =>
            m.aggregated_downline_debt ? formatNumber(m.aggregated_downline_debt, {style: 'currency', currency: 'USD'}) : null,
        field: 'aggregated_downline_debt',
        title: localized.aggregated_downline_debt,
        sortable: true,
    };

    const downline_agents_count: TextColumn<DebtResponsibleMasterAgentViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: m => m.downline_agents_count?.toString(),
        field: 'downline_agents_count',
        title: localized.downline_agents_count,
        sortable: true,
    };

    const remove_button: ButtonColumn<DebtResponsibleMasterAgentViewModel> = {
        width: 150,
        type: 'button',
        valueGetter: m => ({
            title: formatMessage(localized.removeButton),
            onClick: () => {
                openModal({
                    body: (
                        <ActionConfirmationModal
                            title={localized.removeModalTitle}
                            contentDescription={localized.removeModalDescription}
                            handleCancel={closeModal}
                            handleConfirm={() => {
                                removeDebtResponsibleMasterAgent({agent_id: m.agent_uid});
                                closeModal();
                            }}
                            cancelButtonLabel={localized.cancelButtonLabel}
                            confirmButtonLabel={localized.confirmButtonLabel}
                        />
                    ),
                    styleParams: {maxWidth: 'xs'},
                });
            },
        }),
        field: 'remove_button',
    };

    const config: Record<DebtResponsibleMasterAgentColumns, DataGridColumn<DebtResponsibleMasterAgentViewModel>> = {
        agent_uid: agent_id,
        username,
        created_at,
        created_by,
        aggregated_downline_debt,
        downline_agents_count,
        remove_button,
    };

    return columns.map(c => config[c]);
}

type DebtResponsibleMasterAgentDataGridProps = Omit<
    DataGridServerProps<DebtResponsibleMasterAgentViewModel, DebtResponsibleMasterAgentColumns>,
    'columns'
> & {columns: DebtResponsibleMasterAgentColumns[]};

export function DebtResponsibleMasterAgentDataGrid(props: DebtResponsibleMasterAgentDataGridProps) {
    const columns = useColumns(props.columns);

    return <DataGridServer {...props} columns={columns} />;
}
